import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftRight, X } from "lucide-react";
import { toast } from "sonner";
import { dismissCard, getCardDetail, setTransfer } from "@/lib/api";
import {
  TYPE_ACCENT,
  TYPE_GLYPH,
  TYPE_LABELS,
  categoryColor,
  formatSigned,
  formatTime,
  prettyFactLabel,
  prettyFactValue,
  relativeDay,
} from "@/lib/format";

export const CardDetailSheet = ({ card, onClose, onDismissed, onExcluded }) => {
  const [detail, setDetail] = useState(null);
  const [busyTx, setBusyTx] = useState(null);

  useEffect(() => {
    if (card) {
      setDetail(null);
      getCardDetail(card.id)
        .then(setDetail)
        .catch(() => toast.error("Impossibile caricare il dettaglio"));
    }
  }, [card]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (card) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, onClose]);

  const handleDismiss = async () => {
    try {
      await dismissCard(card.id);
      onDismissed(card.id);
    } catch {
      toast.error("Errore di rete: riprova");
    }
  };

  const handleExclude = async (txId) => {
    setBusyTx(txId);
    try {
      await setTransfer(txId, true);
      setDetail((d) => (d ? { ...d, transactions: d.transactions.filter((t) => t.id !== txId) } : d));
      toast.success("Giroconto escluso dalle spese. Ricalcolo l'insight…");
      onExcluded?.();
    } catch {
      toast.error("Errore: riprova");
    } finally {
      setBusyTx(null);
    }
  };

  const facts = detail?.card?.facts || {};
  const txs = detail?.transactions || [];
  const accent = card ? TYPE_ACCENT[card.type] || "#4D9CFF" : "#4D9CFF";

  return (
    <AnimatePresence>
      {card && (
        <div className="fixed inset-0 z-50" data-testid="card-detail-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[85dvh] max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#07070C]"
          >
            <div className="px-5 pb-2 pt-4">
              <div className="flex items-center justify-between">
                <span className="caps-tag" style={{ color: accent }}>
                  <span className="mr-1.5">{TYPE_GLYPH[card.type]}</span>
                  {TYPE_LABELS[card.type] || card.type}
                </span>
                <button
                  onClick={onClose}
                  data-testid="card-detail-close-button"
                  aria-label="Chiudi"
                  className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4">
              <p
                className="mono mt-1 text-[40px] font-light leading-none tracking-[-1.5px] tabular"
                style={{ color: accent }}
              >
                {card.key_number}
              </p>
              <h3 className="mt-3 text-sm font-light leading-[1.55] text-white/80" data-testid="card-detail-title">
                {card.title}
              </h3>
              {card.body && <p className="mt-1 text-xs font-light leading-[1.55] text-white/45">{card.body}</p>}

              {!detail ? (
                <div className="mt-8 flex justify-center gap-2 py-6">
                  <span className="liqo-thinking-dot" />
                  <span className="liqo-thinking-dot" />
                  <span className="liqo-thinking-dot" />
                </div>
              ) : (
                <>
                  {Object.keys(facts).length > 0 && (
                    <div className="mt-6">
                      <p className="caps-tag text-white/35">I numeri</p>
                      <div className="mt-2 overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14]">
                        {Object.entries(facts).map(([k, v], i) => (
                          <div
                            key={k}
                            className={`flex items-start justify-between gap-4 px-4 py-3 ${i > 0 ? "border-t border-white/[0.04]" : ""}`}
                          >
                            <span className="text-xs font-light text-white/45">{prettyFactLabel(k)}</span>
                            <span className="mono text-right text-xs font-light tabular text-white/85">
                              {prettyFactValue(k, v)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {txs.length > 0 && (
                    <div className="mt-5">
                      <p className="caps-tag text-white/35">Transazioni collegate</p>
                      <div className="mt-2 overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14]">
                        {txs.map((t, i) => (
                          <div
                            key={t.id}
                            className={`flex items-center justify-between gap-3 px-4 py-3 ${i > 0 ? "border-t border-white/[0.04]" : ""}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[13px] font-light text-white/85">{t.description}</p>
                              <p className="caps-tag mt-1" style={{ fontSize: "9px" }}>
                                <span className="text-white/30">{relativeDay(t.date)} · {formatTime(t.date)} · </span>
                                <span style={{ color: categoryColor(t.category) }}>{t.category}</span>
                              </p>
                              <button
                                onClick={() => handleExclude(t.id)}
                                disabled={busyTx === t.id}
                                data-testid="card-tx-exclude-button"
                                className="caps-tag mt-2 inline-flex items-center gap-1 rounded-[5px] border border-white/[0.1] px-2 py-1 text-white/45 hover:bg-white/[0.05] hover:text-white/75 active:scale-[0.97] disabled:opacity-40"
                                style={{ fontSize: "9px" }}
                              >
                                <ArrowLeftRight size={10} />
                                {busyTx === t.id ? "Escludo…" : "È un giroconto"}
                              </button>
                            </div>
                            <span
                              className={`mono shrink-0 self-start text-[13px] font-light tabular ${t.amount > 0 ? "text-[#00FF87]" : "text-white/85"}`}
                            >
                              {formatSigned(t.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-white/[0.06] px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <button
                onClick={handleDismiss}
                data-testid="card-detail-dismiss-button"
                className="h-11 w-full rounded-[6px] border border-white/[0.15] bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-white/85 hover:bg-white/[0.05] active:scale-[0.98]"
              >
                Ok, ho capito
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
